import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  groupBy,
  isCompositeFilterDescriptor,
  State,
  toODataString,
} from '@progress/kendo-data-query'
import { isColumnMenuFilterActive, isColumnMenuSortActive } from '@progress/kendo-react-grid'


import { UseGetOdata } from './embarcacionesServices'
import { escapeODataString } from './odataEscape'
import { getReservasTipoEstados, totalEstadosReserva } from './reservasServices'
import { roundHalfAwayFromZero } from './roundMoney'
import { toDate } from './utilsDate'

import { ESTADOS_BOOKING_OPERABLES } from '@/common/constants/default'
import { COMPONENT_TYPE } from '@/common/constants/labels'
import { Indicador, ObjetoDividido } from '@/common/interfaces/commons'
import { PathEstado } from '@/common/interfaces/general'
import { ListaSeleccion } from '@/common/interfaces/listaSeleccion'
import { IMatriz } from '@/common/interfaces/matriz'
import { ItemProveedor, MatrizSalida, ProveedorAsignadoEmbarcacion } from '@/common/interfaces/matrizAsignacionSalida'

import { Proveedor } from '@/common/interfaces/proveedor'
import { Reserva } from '@/common/interfaces/reserva'
import { IReservaDetalleEmitido } from '@/common/interfaces/reservaDetalleEmitido'
import { ActividadReserva } from '@/common/interfaces/respuestas'
import { ROLE } from '@/common/interfaces/roles'

import { ListaSeleccionTree, Seleccion } from '@/core/store/catalogos/CatalogosReducer'

import { BookingCard } from '@/modules/Reservas/components/BookingsCards'

export const boolenaToText = (value: boolean): string => {
  return value ? 'Si' : 'No'
}

/**
 * Si el objeto está vacio retorna TRUE
 * @param {*} obj
 * @returns boolean
 */
export const esObjetoVacio = (obj: object | null | undefined): boolean => {
  // Validar que obj no sea null o undefined
  if (obj === null || obj === undefined) {
    return true
  }

  return Object.keys(obj).length === 0
}

/**
 * Retorna true o false dependiendo del estadoId en el que se encuentra
 * @param estadosId
 * @returns
 */
export const estadoReservaAcciones = (estadosId: number[], estadosReserva: any): boolean => {
  let respuesta = false

  estadosId.forEach((e) => {
    if (e === estadosReserva['Negado'].EstadoId || e === estadosReserva['Reservado'].EstadoId) {
      respuesta = false
    }
    if (
      e === estadosReserva['Modificación Pendiente'].EstadoId ||
      e === estadosReserva['Reserva Pendiente'].EstadoId ||
      e === estadosReserva['Cancelación Pendiente'].EstadoId ||
      e === estadosReserva['Lista de Espera'].EstadoId
    ) {
      respuesta = true
    }
  })

  return respuesta
}

export const contains = (text: string, term: string): boolean => {
  return text.toLowerCase().indexOf(term.toLowerCase()) >= 0
}

export const proveedorCheckNullNombreCompleto = (proveedor: []): Proveedor[] => {
  return proveedor.map((p: Proveedor) => ({
    ...p,
    NombreCompleto: p.NombreCompleto ?? 'No definido',
  }))
}

export const colorByTipo = (tipo: string): string => {
  let color: string

  switch (tipo) {
    case 'Charter':
      color = 'rgba(187,252,178,1)'
      break

    case 'Dock':
      color = 'rgba(127,211,223,1)'
      break

    case 'Close':
      color = 'rgba(226,216,169,1)'
      break

    default:
      color = ''
      break
  }

  return color
}

export const colorCellGuiaSelected = (cell: ItemProveedor, dataItem: MatrizSalida, noHabilitado: boolean): string => {
  let color = ''

  if (noHabilitado) {
    color = 'orange'
  } else if (cell.estado === 1) {
    const proSelect = dataItem.proveedorAsignadoEmbarcacions.find((p) => p.proveedorId === cell.proId)

    if (proSelect?.fechaInicio && proSelect?.fechaFin) {
      color = getColorByIntervalo(dataItem.duracionSalida, proSelect.fechaInicio, proSelect.fechaFin)
    } else {
      color = '#28a745'
    }
  }

  return color
}

const getColorByIntervalo = (duracionSalida: number, fInicio: Date, fFin: Date): string => {
  let color = ''
  const duracionSelect = getDuracionDias(fInicio, fFin)

  if (duracionSelect < duracionSalida) {
    color = '#419AFF'
  }
  if (duracionSelect === duracionSalida) {
    color = '#28a745'
  }

  return color
}

export const getDuracionDias = (inicio: Date, fin: Date): number => {
  // Diferencia exacta con fracción, igual que moment.duration(diff).asDays()
  return (toDate(fin).getTime() - toDate(inicio).getTime()) / 86400000
}

/**
 * Agrega campos computados con sufijo 'Date' (fecha a medianoche) sin modificar
 * los campos originales. Permite que la columna filtre por el campo *Date
 * mientras el cell renderer muestra el campo original con hora.
 *
 * Uso:
 *   const data = addDateOnlyFields(items, ['fechaCreacion', 'fechaUltimaModificacion'])
 *   // Resultado: cada item tendrá fechaCreacionDate y fechaUltimaModificacionDate
 */

export const addDateOnlyFields = <T extends Record<string, any>>(items: T[], fields: string[]): T[] => {
  if (!items?.length || !fields?.length) {
    return items
  }

  return items.map((item) => {
    const result = { ...item }

    for (const field of fields) {
      const value = item[field]

      if (value) {
        const raw = value as string
        const normalized = typeof raw === 'string' && !raw.includes('T') ? `${raw}T00:00:00` : raw
        const date = value instanceof Date ? value : new Date(normalized)

        if (!Number.isNaN(date.getTime())) {
          ;(result as Record<string, unknown>)[`${field}Date`] = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
          )
        }
      }
    }

    return result
  })
}

/**
 * Agrega campos computados con sufijo 'Rounded' (valor redondeado) sin modificar
 * los campos originales. Permite que la columna filtre por el campo *Rounded
 * mientras el valor original se preserva para cálculos y envío al backend.
 *
 * Uso:
 *   const data = addRoundedNumericFields(items, { costoPactadoUsd: 2, tasaCambio: 4 })
 *   // Resultado: cada item tendrá costoPactadoUsdRounded y tasaCambioRounded
 */

export const addRoundedNumericFields = <T extends Record<string, any>>(
  items: T[],
  fieldsDecimals: Record<string, number>,
): T[] => {
  if (!items?.length) {
    return items
  }

  const entries = Object.entries(fieldsDecimals)

  return items.map((item) => {
    const result = { ...item }

    for (const [field, decimals] of entries) {
      const value = item[field]

      if (typeof value === 'number') {
        ;(result as Record<string, unknown>)[`${field}Rounded`] = roundHalfAwayFromZero(value, decimals)
      }
    }

    return result
  })
}

export { roundHalfAwayFromZero, roundMoney } from './roundMoney'

export const isColumnFilterActive = (field: string, dataState: State): boolean => {
  // Usar la función de Kendo para verificar filtros y ordenamiento
  const kendoFilterActive = isColumnMenuFilterActive(field, dataState.filter)
  const kendoSortActive = isColumnMenuSortActive(field, dataState.sort)

  // Verificación adicional manual para filtros que Kendo puede no detectar
  const hasManualFilter = hasFieldFilter(field, dataState.filter)

  return kendoFilterActive || kendoSortActive || hasManualFilter
}

/**
 * Busca recursivamente si existe un filtro para el campo especificado
 */
const hasFieldFilter = (field: string, filter: any): boolean => {
  if (!filter) {
    return false
  }

  // Si es un filtro directo con el campo
  if (filter.field === field) {
    return isActiveFilterDescriptor(filter)
  }

  // Si tiene filtros anidados, buscar recursivamente
  if (filter.filters && Array.isArray(filter.filters)) {
    return filter.filters.some((f: any) => hasFieldFilter(field, f))
  }

  return false
}

const isActiveFilterDescriptor = (filterDescriptor: any): boolean => {
  if (filterDescriptor?.field === undefined) {
    return false
  }

  const {operator} = filterDescriptor
  const {value} = filterDescriptor

  // Operadores que son activos sin valor explícito
  if (operator === 'isnull' || operator === 'isnotnull' || operator === 'isempty' || operator === 'isnotempty') {
    return true
  }

  if (value === undefined || value === null) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  return true
}

export const isFilterActive = (field: string, dataState: State): string => {
  return isColumnFilterActive(field, dataState) ? 'k-sorted' : ''
}

/**
 * Detecta si hay algún filtro de columna activo en el dataState
 * @param dataState Estado del grid de Kendo
 * @returns boolean - true si hay filtros de columna activos
 */
export const hasActiveColumnFilters = (dataState: State): boolean => {
  if (!dataState) {
    return false
  }

  return !!dataState.filter?.filters?.length
}

/**
 * Cuantos proveedores de una salida cuentan como «guia» en la matriz.
 *
 * Los Guia Embarcacion siempre; los Jefe Expedicion solo si la salida esta marcada con
 * `contarJefeExpedicion`. El resto —Fotografo, por ejemplo— NUNCA cuenta.
 *
 * Existe para que el alta y la baja usen la misma regla. La baja restaba uno a mano con una
 * condicion que no era la inversa del alta, asi que desasignar un Fotografo en una salida con
 * `contarJefeExpedicion` descontaba un guia que nunca se habia sumado.
 */
export const contarGuias = (salida: MatrizSalida): number => {
  const { numGuias, numJefeExp } = sumaAsignados(salida)

  return sumarSi(salida.contarJefeExpedicion, numGuias, numJefeExp)
}

export const sumaAsignados = (
  matrizActivaProcesando: MatrizSalida,
): {
  numGuias: number
  numJefeExp: number
  otros: number
} => {
  let numGuias = 0
  let numJefeExp = 0
  let otros = 0

  matrizActivaProcesando.proveedorAsignadoEmbarcacions.forEach((prov: ProveedorAsignadoEmbarcacion) => {
    switch (prov.tipoProveedor) {
      case 'Guía Embarcación':
        numGuias += 1
        break

      case 'Jefe Expedición':
        numJefeExp += 1
        break

      default:
        otros += 1
        break
    }
  })

  return { numGuias, numJefeExp, otros }
}

export const sumarSi = (isSumar: boolean, a: number, b: number): number => {
  return isSumar ? a + b : a
}

interface ITodosProveedores {
  todosJefesExp: string
  todosGuias: string
  todosFotografos: string
  otros: string
}

export const todosProveedores = (dataArreglo: MatrizSalida[]): ITodosProveedores => {
  let todosJefesExp = ''
  let todosGuias = ''
  let todosFotografos = ''
  let otros = ''

  dataArreglo.forEach((data: MatrizSalida) => {
    const idiomaSelect = data.idioma

    data.proveedorAsignadoEmbarcacions.forEach((prov: ProveedorAsignadoEmbarcacion) => {
      switch (prov.tipoProveedor) {
        case 'Guía Embarcación':
          todosGuias += prov.nombre + '-' + idiomaSelect + '|'
          break

        case 'Jefe Expedición':
          todosJefesExp += prov.nombre + '-' + idiomaSelect + '|'
          break

        case 'Fotógrafo':
          todosFotografos += prov.nombre + '|'
          break

        default:
          otros += prov.nombre + '|'
          break
      }
    })
  })

  return { todosJefesExp, todosGuias, todosFotografos, otros }
}

interface IAgruparPor<T> {
  [key: string]: T[]
}

export const agruparPor = <T>(array: T[], predicate: (v: T) => any): IAgruparPor<T> =>
  array.reduce(
    (acc, value) => {
      ;(acc[predicate(value)] ||= []).push(value)

      return acc
    },
    {} as { [key: string]: T[] },
  )

export const isProveedorActivoByAll = (all: string[], selected: string): boolean => {
  return all.find((p) => p === selected) === undefined ? false : true
}

export const agruparCatalogos = (data: any): any => {
  const estadoAgrupado = groupBy(data, [{ field: 'Ambito' }])
  const estados: any = {}

  estadoAgrupado.forEach((ambito: any) => {
    const detalle: any = {}

    ambito.items.forEach((estado: any) => {
      detalle[`${estado.Nombre}`] = { ...estado }
    })
    estados[`${ambito.value}`] = detalle
  })

  return estados
}

export const agruparListaSeleccionPorTabla = (data: Seleccion[]): Record<string, any> => {
  const keys = Object.keys(data)
  const splitKeys = keys.map((k) => k.split('.'))
  const resultado: Record<string, any> = {}

  splitKeys.forEach((lista) => {
    let tempObj: Record<string, any> = resultado

    lista.forEach((clave, index) => {
      if (!tempObj[clave]) {
        tempObj[clave] = {}
      }
      if (index < lista.length - 1) {
        tempObj = tempObj[clave]
      }
    })
  })

  return resultado
}

export const agruparSalidas = (data: any): any => {
  const salidasAgrupadas = groupBy(data, [{ field: 'salidaEmbarcacionId' }])
  const result: any = {}

  salidasAgrupadas.forEach((sEmbarcacion: any) => {
    let detalle: any = {}
    let filas = 0

    sEmbarcacion.items.forEach((se: any) => {
      const { ...rest } = se

      detalle = { ...rest }
      filas += 1
    })
    result[`${sEmbarcacion.value}`] = { ...detalle, filas }
  })

  return result
}

export const idNombre = (data: any, reservaWizzar: Map<string, PathEstado>): any => {
  const idEstadoNombre: any = {}

  data.forEach((d: any) => {
    const siglas = d.Nombre.split(' ')
      .map((s: string) => s.charAt(0).toLocaleUpperCase())
      .join('')

    idEstadoNombre[`${d.EstadoId}`] = {
      nombre: d.Nombre,
      siglas: siglas.substring(0, 3),
      color: nombreEstadoToColorReservaWizzard(d.Nombre, reservaWizzar),
      colorInactivo: nombreEstadoToColorReservaWizzardInactivo(d.Nombre, reservaWizzar),
    }
  })

  return idEstadoNombre
}

export const idListaSeleccion = (lista: ListaSeleccion[]): any => {
  const idLista: any = {}

  lista.forEach((l: ListaSeleccion) => {
    idLista[`${l.ListaSeleccionId}`] = l
  })

  return idLista
}

export const buildListaSeleccionTree = (lista: ListaSeleccion[]): any => {
  const tree: ListaSeleccionTree[] = []
  const agruparPorAmbito = groupBy(lista, [{ field: 'Ambito' }])

  agruparPorAmbito.forEach((ambito: any) => {
    const items: ListaSeleccionTree[] = []

    ambito.items.forEach((item: any) => {
      items.push({ text: item.Nombre, id: item.ListaSeleccionId, items: [] })
    })
    tree.push({ text: ambito.value, id: 0, items: items })
  })

  return tree
}

const nombreEstadoToColorReservaWizzard = (nombre: string, reservaWizzard: Map<string, PathEstado>): string => {
  let colorValue = ''

  reservaWizzard.forEach((value: PathEstado) => {
    if (value.name === nombre) {
      colorValue = value.color
    }
  })

  return colorValue
}
const nombreEstadoToColorReservaWizzardInactivo = (nombre: string, reservaWizzard: Map<string, PathEstado>): string => {
  let color = ''

  reservaWizzard.forEach((value: PathEstado) => {
    if (value.name === nombre) {
      color = value.colorInactivo
    }
  })

  return color
}

export const sumTotal = (item: any, field: string): any => {
  return item.Disponibilidad.reduce((acc: any, current: { [x: string]: any }) => acc + current[field], 0)
}

export const sumTotalPax = (item: any, field: string): any => {
  for (const i in item.Disponibilidad) {
    item.Disponibilidad[i].TotalPaxAllotmentFooter =
      item.Disponibilidad[i].DisponibilidadRestanteAllotment * item.Disponibilidad[i].CantidadPasajeros
  }
  const sumPaxVendidas = item.Disponibilidad.map((c: any) => c.TotalPaxCabinaTomada).reduce(
    (prev: any, curr: any) => prev + curr,
    0,
  )
  const sumPax = item.Disponibilidad.reduce(
    (
      acc: any,
      current: {
        [x: string]: any
      },
    ) => acc + current['TotalPaxAllotmentFooter'],
    0,
  )

  return field === 'DisponibilidadRestanteAllotment' ? sumPax : item.Aforo - sumPax - sumPaxVendidas
}

export const definirAmbito = (nombre = '', editar = false): any => {
  let definicion

  switch (nombre) {
    case 'Experiencia':
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor experiencia`,
        ambito: 'Experiencia',
      }
      break

    case 'Proveedor.Especialidad.Experiencia':
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor experiencia`,
        ambito: 'Experiencia',
      }
      break

    case 'PMTS':
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor PMTS`,
        ambito: 'PMTS',
      }
      break

    case 'Experiencia GPS':
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor experiencia GPS`,
        ambito: 'Experiencia.GPS',
      }
      break

    case 'Proveedor.Especialidad.Experiencia.GPS':
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor experiencia GPS`,
        ambito: 'Experiencia.GPS',
      }
      break

    default:
      definicion = {
        title: `${editar ? 'Editar' : 'Añadir'} proveedor PMTS`,
        ambito: 'PMTS',
      }
      break
  }

  return definicion
}

export const esPMTS = (nombre: string): boolean => {
  return nombre === undefined ? false : nombre === 'PMTS'
}

export const tieneAccesos = (userRoles: string[], accesosRoles: string[]): boolean => {
  let conAcceso = false
  const variosAccesos: boolean[] = []

  userRoles.forEach((ur) => {
    if (accesosRoles.includes(ur)) {
      variosAccesos.push(true)
    } else {
      variosAccesos.push(false)
    }
  })
  conAcceso = variosAccesos.includes(true) ? true : false

  return conAcceso
}

export const esAutenticado = (msal: any): boolean => {
  let user

  const isAuth = !!msal.getActiveAccount()

  if (isAuth) {
    user = { loggedIn: true }
  } else {
    user = { loggedIn: false }
  }

  return user && user.loggedIn
}

export const filtroEspecial = (ds: State): string => {
  const buscarEspecial = tieneFiltroEspecial(ds)

  // Validar que el valor no sea null, vacío o solo espacios
  if (buscarEspecial !== null && buscarEspecial.trim() !== '') {
    // El texto sale del filtro «Personalizado» que teclea el usuario, así que va escapado: sin esto,
    // buscar un ejecutivo con apóstrofo en el apellido —«D'Angelo»— cerraba el literal OData, el
    // backend respondía 400 y el grid de Bookings quedaba vacío sin explicación. Es además el vector
    // de inyección que el helper existe para cerrar.
    return `EjecutivoComercial/AsignacionEjecutivoOperativo/any(ao:contains(ao/AgenteOperativo/NombreCompleto,'${escapeODataString(buscarEspecial)}'))`
  }

  return ''
}

const tieneFiltroEspecial = (ds: State): string => {
  const buscarFiltro = (filters: any[]): string => {
    for (const filtro of filters) {
      if (filtro?.field?.includes('Personalizado')) {
        return filtro.value
      }
      if (filtro.filters) {
        const resultado = buscarFiltro(filtro.filters)

        if (resultado) {
          return resultado
        }
      }
    }

    return ''
  }

  if (ds.filter?.filters) {
    return buscarFiltro(ds.filter.filters)
  }

  return ''
}

export const eliminarFiltroEspecial = (ds: State): State => {
  const newState = structuredClone(ds)
  const eliminarRecursivo = (filters: any[]): any[] => {
    return filters
      .map((filter) => {
        if (filter.filters) {
          filter.filters = eliminarRecursivo(filter.filters)
        }

        // Elimina el filtro si el field incluye "Personalizado"
        return filter?.field?.includes('Personalizado') ? null : filter
      })
      .filter((filter) => filter !== null && (!filter.filters || filter.filters.length > 0))
  }

  if (newState.filter) {
    newState.filter.filters = eliminarRecursivo(newState.filter.filters)
  }

  return newState
}

export const fusionarFiltros = (dataString: string, stringFilter: string): string => {
  // Si no hay filtro especial, retornar la cadena original
  if (stringFilter === '' || stringFilter.trim() === '') {
    return dataString
  }

  let newDataString = ''

  if (dataString.includes('$filter=')) {
    // Buscar el índice de inicio del filtro
    const filterStartIndex = dataString.indexOf('$filter=')

    // Buscar el índice del siguiente parámetro (puede ser $orderby, $skip, $top, etc.)
    // Si no existe ninguno, tomar hasta el final
    let filterEndIndex = dataString.length
    const nextParams = ['&$orderby', '&$skip', '&$top', '&$count', '&$expand']

    for (const param of nextParams) {
      const paramIndex = dataString.indexOf(param, filterStartIndex)

      if (paramIndex !== -1 && paramIndex < filterEndIndex) {
        filterEndIndex = paramIndex
      }
    }

    // Extraer el filtro existente
    const filtroExistente = dataString.substring(filterStartIndex + 8, filterEndIndex)

    // Crear el nuevo filtro combinado
    const nuevoFiltro = `$filter=(${filtroExistente} and ${stringFilter})`

    // Reemplazar el filtro antiguo con el nuevo
    newDataString = dataString.substring(0, filterStartIndex) + nuevoFiltro + dataString.substring(filterEndIndex)
  } else {
    // Si no hay filtro, agregarlo al final
    newDataString = `${dataString}&$filter=${stringFilter}`
  }

  return newDataString
}


const buscarFiltroCampo = (
  filters: Array<FilterDescriptor | CompositeFilterDescriptor>,
  campo: string,
): FilterDescriptor | null => {
  for (const filtro of filters) {
    if (isCompositeFilterDescriptor(filtro)) {
      const resultado = buscarFiltroCampo(filtro.filters, campo)

      if (resultado) {
        return resultado
      }
    } else if (filtro.field === campo) {
      return filtro
    }
  }

  return null
}

const PATRON_VALOR_NOMBRE = /^(.*)\s*\(([^()]+)\)\s*$/

const clausulaParaCampo = (filtro: FilterDescriptor, campo: string, valor: string): string =>
  toODataString({ filter: { logic: 'and', filters: [{ ...filtro, field: campo, value: valor }] } }).replace(
    '$filter=',
    '',
  )

export const filtroSalesProfit = (ds: State): string => {
  const filtro = ds.filter?.filters ? buscarFiltroCampo(ds.filter.filters, 'SalesProfit.Valor') : null

  if (!filtro) {
    return ''
  }

  const valorBuscado = typeof filtro.value === 'string' ? filtro.value.trim() : ''
  const match = PATRON_VALOR_NOMBRE.exec(valorBuscado)

  if (match) {
    const [, parteValor, parteNombre] = match

    return `(${clausulaParaCampo(filtro, 'SalesProfit.Valor', parteValor.trim())} and ${clausulaParaCampo(filtro, 'SalesProfit.Nombre', parteNombre.trim())})`
  }

  return `(${clausulaParaCampo(filtro, 'SalesProfit.Valor', valorBuscado)} or ${clausulaParaCampo(filtro, 'SalesProfit.Nombre', valorBuscado)})`
}

export const eliminarFiltroSalesProfit = (ds: State): State => {
  const newState = structuredClone(ds)
  const eliminarRecursivo = (
    filters: Array<FilterDescriptor | CompositeFilterDescriptor>,
  ): Array<FilterDescriptor | CompositeFilterDescriptor> => {
    return filters
      .map((filtro) => {
        if (isCompositeFilterDescriptor(filtro)) {
          return { ...filtro, filters: eliminarRecursivo(filtro.filters) }
        }

        return filtro.field === 'SalesProfit.Valor' ? null : filtro
      })
      .filter(
        (filtro): filtro is FilterDescriptor | CompositeFilterDescriptor =>
          filtro !== null && (!isCompositeFilterDescriptor(filtro) || filtro.filters.length > 0),
      )
  }

  if (newState.filter) {
    newState.filter.filters = eliminarRecursivo(newState.filter.filters)
  }

  return newState
}

/**
 * Simplifica las fechas en formato ISO completo a formato simple YYYY-MM-DD en cadenas OData
 * Ejemplo: 2025-10-18T00:00:00.000Z -> 2025-10-18
 * @param odataString - Cadena OData que puede contener fechas en formato ISO
 * @returns Cadena OData con fechas simplificadas
 */
export const simplificarFechasOData = (odataString: string): string => {
  // Regex para encontrar fechas en formato ISO: YYYY-MM-DDTHH:mm:ss.sssZ
  // Captura solo la parte de la fecha (YYYY-MM-DD) y descarta el resto
  const isoDateRegex = /(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z/g

  return odataString.replaceAll(isoDateRegex, '$1')
}

interface IAccesosList {
  accesos: ROLE[]
}

export const handlePermisosCarpeta = (nombreCarpeta: string): IAccesosList => {
  switch (nombreCarpeta) {
    // Casos agrupados: varias carpetas comparten el mismo conjunto de accesos
    case 'Boletos':

    case 'Boletos Aéreos':
      return { accesos: [ROLE.AdministradorSistema, ROLE.Reservas] }

    case 'Cruceros MT':

    case 'Alojamiento':

    case 'Hotel Finch Bay':

    case 'Producto Propio':
      return {
        accesos: [ROLE.AdministradorSistema, ROLE.Reservas, ROLE.Operaciones],
      }

    case 'Reservas':
      return {
        accesos: [ROLE.AdministradorSistema, ROLE.Reservas, ROLE.ReservasRegional],
      }

    case 'Comercial':
      return { accesos: [ROLE.AdministradorSistema, ROLE.Comercial] }

    case 'Finanzas':
      return {
        accesos: [ROLE.AdministradorSistema, ROLE.Finanzas, ROLE.FinanzasRegional],
      }

    case 'Guias Embarcaciones':
      return { accesos: [ROLE.AdministradorSistema, ROLE.OperacionesMaritimas] }

    case 'Operaciones Land':
      return {
        accesos: [ROLE.AdministradorSistema, ROLE.Operaciones, ROLE.Reservas],
      }

    case 'Yield':
      return {
        accesos: [ROLE.AdministradorSistema, ROLE.YieldManagement, ROLE.Reservas],
      }

    case 'Gerencia Comercial':
      return { accesos: [ROLE.AdministradorSistema, ROLE.GerenciaComercial] }

    case 'PMTS':
      return { accesos: [ROLE.AdministradorSistema, ROLE.PMTS] }

    case 'Histórico Cartas Reserva':
      return { accesos: [ROLE.AdministradorNegocio, ROLE.AdministradorSistema, ROLE.Reservas] }

    default:
      return { accesos: [ROLE.AdministradorSistema] }
  }
}

/**
 * Determines if a user role has access to a specific dashboard.
 *
 * @param {string} rol - The role of the user to check access for.
 * @param {string} dashboard - The name of the dashboard to check access against.
 * @returns {boolean} - Returns `true` if the role has access to the dashboard, otherwise `false`.
 *
 */
export const handlePermisosDashboard = (rol: string[] | undefined, dashboard: string): boolean => {
  if (!rol) {
    return false
  }

  const accesosDashboards: Record<string, string[]> = {
    DashboardOperaciones: [ROLE.Operaciones, ROLE.AdministradorSistema, ROLE.OperacionesMaritimas],
    DashboardReservas: [ROLE.Reservas, ROLE.ReservasRegional, ROLE.GerenciaReservas, ROLE.AdministradorSistema],
    // Bloque «Operación Semanal» del inicio de Perú: solo gerencia. El equipo operativo
    // (ROLE.Operaciones) ve el resto del inicio pero NO este consolidado semanal.
    DashboardOperacionSemanal: [ROLE.AdministradorNegocio, ROLE.AdministradorSistema],
  }

  const rolesPermitidos = accesosDashboards[dashboard]

  for (const rolPermitido of rolesPermitidos) {
    if (rol.includes(rolPermitido)) {
      return true
    }
  }

  return false
}

// Función que extrae los códigos de reserva y
// los números de boletos de la tabla de reservas emitidas y remitidas.
// Esta función se utiliza para aplicar filtros en ambas pantallas.
export const extraerBookingCodes = (data: any): any => {
  return data.map((item: any) => {
    const bookingCodes = JSON.stringify(
      item.ReservaDetalle.map((detalle: any) => detalle.Actividad.ActividadMaestra.BookingCode),
    )
    const boletos = JSON.stringify(item.ReservaDetalle.map((detalle: any) => detalle.NumeroBoleto))

    return { ...item, bookingCodes, boletos }
  })
}

// Funcion utilizada para dar formato de fecha a las fechas
//  de las reservas emitidas y remitidas
export const formatDateRB = (data: Array<IReservaDetalleEmitido>): IReservaDetalleEmitido[] => {
  return data.map((item: IReservaDetalleEmitido) => {
    item.AsignacionPasajero.Pasajero.FechaNacimiento = toDate(item.AsignacionPasajero.Pasajero.FechaNacimiento)
    item.FechaInicio = toDate(item.FechaInicio)
    item.FechaFin = toDate(item.FechaFin)
    item.FechaEmision = toDate(item.FechaEmision)

    return item
  })
}

export const hasRole = (userRoles: string[], requiredRoles: string[]): boolean => {
  return requiredRoles.some((role) => userRoles.includes(role))
}

export const desahabilitaAsignacionesActividad = (actividad: IMatriz[]): boolean => {
  if (actividad.length === 2) {
    return esTkTDirecto(actividad[0]) || esTkTDirecto(actividad[1])
  }

  return true
}

export const desahabilitaAsignacionActividad = (actividad: IMatriz): boolean => {
  if (!noEsGenerico(actividad)) {
    return false
  }
  if (esTkTDirecto(actividad)) {
    return true
  }

  return grupoEsNull(actividad)
}

const grupoEsNull = (actividad: IMatriz): boolean => {
  return actividad.grupo === null
}

export const esOW = (actividad: IMatriz): boolean => {
  return actividad.serviceCode!.includes('OW')
}

export const esNewRT = (actividad: IMatriz): boolean => {
  return actividad.serviceCode!.includes('RT')
}

export const esNewOW = (actividad: IMatriz): boolean => {
  return esOW(actividad) && !grupoEsNull(actividad)
}

const noEsGenerico = (actividad: IMatriz): boolean => {
  if (!actividad.serviceCode) {
    return true
  }

  return (
    actividad?.serviceCode?.split('_')[0] !== 'GSC' &&
    actividad?.serviceCode?.split('_')[0] !== 'GSN' &&
    actividad?.serviceCode?.split('_')[0] !== 'LAN'
  )
}

const esTkTDirecto = (actividad: IMatriz): boolean => {
  if (!actividad.serviceCode) {
    return true
  }

  return esTkT(actividad.tipoComponente) && actividad.serviceCode.includes('DIR')
}

export const esTkT = (tipoComponente: string | undefined): boolean => {
  return tipoComponente === COMPONENT_TYPE.TICKET
}

export const tieneGrupo = (actividad: IMatriz): boolean => {
  return actividad.grupo !== 0
}

/** true si el estado del booking permite operar sus actividades en la matriz (ver ESTADOS_BOOKING_OPERABLES). */
export const estadoBookingOperable = (estadoBooking?: string): boolean => {
  return estadoBooking !== undefined && ESTADOS_BOOKING_OPERABLES.includes(estadoBooking)
}

/** true cuando el booking de la actividad NO está en un estado que permita operarla. */
export const bookingNoOperable = (actividad: IMatriz): boolean => {
  return !estadoBookingOperable(actividad.estadoBooking)
}

/**
 * Verifica si la actividad tiene un componente con proveedor genérico.
 * Se usa para habilitar la opción "Asignación actividad operativa" en el menú contextual.
 * @param actividad - Actividad de la matriz
 * @returns true si el proveedor es genérico
 */
export const tieneProveedorGenerico = (actividad: IMatriz): boolean => {
  return actividad.esGenerico === true
}

export const actividadFechasIdenticasInicioFin = (actividad: IMatriz): boolean => {
  return actividad.fechaServicioInicio?.toString() !== actividad.fechaServicioFin?.toString()
}

export const sinEstado = (estado: number): boolean => {
  return estado === 0
}

export const mapActividadReservaToBookingCard = (b: ActividadReserva): BookingCard => {
  return {
    ReservaId: b.actividadReservaCabeceraId,
    ActividadId: b.actividadId,
    Booking: {
      BookingId: b.bookingId,
      BookingCode: b.bookingCode,
      EjecutivoComercialNames: b.ejecutivoComercial,
      AgenteOperativoNames: b.agenteOperativo,
      BloqueoReserva: b.bloqueoReserva,
    },
    Cliente: {
      ClienteNames: b.nombreCliente,
    },
    Estado: {
      Nombre: b.estado,
      EstadoId: b.estadoId,
    },
    Proveedor: {
      NombreProveedor: b.nombreProveedor,
      ProveedorId: b.proveedorId,
    },
  }
}

export const mapBookingCardToActividadReserva = (b: BookingCard): ActividadReserva => {
  return {
    actividadId: b.ActividadId!,
    actividadReservaCabeceraId: b.ReservaId,
    agenteOperativo: b.Booking.AgenteOperativoNames,
    bloqueoReserva: b.Booking.BloqueoReserva,
    bookingCode: b.Booking.BookingCode,
    bookingId: b.Booking.BookingId,
    ejecutivoComercial: b.Booking.EjecutivoComercialNames,
    estado: b.Estado.Nombre,
    estadoId: b.Estado.EstadoId,
    nombreCliente: b.Cliente.ClienteNames,
    nombreProveedor: b.Proveedor!.NombreProveedor,
    proveedorId: b.Proveedor!.ProveedorId,
    selected: b.Seleccionado!,
  }
}

export const mapReservaToBookingCard = (r: Reserva): BookingCard => {
  return {
    ReservaId: r.ReservaId,
    ActividadId: 0,
    Booking: {
      BookingId: r.BookingId,
      BookingCode: r.Booking.BookingCode,
      EjecutivoComercialNames: r.Booking.EjecutivoComercialNames,
      AgenteOperativoNames: r.Booking.AgenteOperativoNames,
      BloqueoReserva: r.Booking.Bloqueado,
    },
    Cliente: {
      ClienteNames: r.Booking.ClienteNames,
    },
    Estado: {
      Nombre: r.Estado.Nombre,
      EstadoId: r.EstadoId,
    },
    Proveedor: {
      NombreProveedor: r.Proveedor.NombreCompleto,
      ProveedorId: r.ProveedorId,
    },
    Reserva: r,
  }
}

export const mapBookingCardToReserva = (b: BookingCard): Reserva => {
  return b.Reserva!
}

export const widthPorcentToColumn = (porcent: number): number => {
  switch (porcent) {
    case 10:
      return 1

    case 20:
      return 2

    case 30:
      return 3

    case 40:
      return 4

    case 50:
      return 6

    case 60:
      return 8

    case 70:
      return 9

    case 80:
      return 10

    case 90:
      return 11

    case 100:
      return 12

    default:
      return 2
  }
}

export const dividirGrupo = (elementos: Indicador[], cantidad = 3): ObjetoDividido => {
  const dimencion = Math.ceil(elementos.length / cantidad)

  return {
    ladoA: elementos.slice(0, dimencion),
    ladoB: elementos.slice(dimencion, 2 * dimencion),
    ladoC: elementos.slice(2 * dimencion),
  }
}

interface ITotalesReservasByEstados {
  reservationPendingHotel: any
  reservationPendingFood: any
}

export const totalesReservasByEstados = async (estados: any): Promise<ITotalesReservasByEstados> => {
  const reservationStateH = await getReservasTipoEstados(
    'Hotel',
    [
      estados['Reserva']['Reserva Pendiente'].EstadoId,
      estados['Reserva']['Modificación Pendiente'].EstadoId,
      estados['Reserva']['Cancelación Pendiente'].EstadoId,
    ],
    '',
    estados['Reserva'],
  )
  const rth = totalEstadosReserva(reservationStateH, estados['Reserva'], [])
  const reservationPendingHotel = rth?.rp ?? 0
  const reservationStateF = await getReservasTipoEstados(
    'Alimento',
    [
      estados['Reserva']['Reserva Pendiente'].EstadoId,
      estados['Reserva']['Modificación Pendiente'].EstadoId,
      estados['Reserva']['Cancelación Pendiente'].EstadoId,
    ],
    '',
    estados['Reserva'],
  )
  const rtf = totalEstadosReserva(reservationStateF, estados['Reserva'], [])
  const reservationPendingFood = rtf?.rp ?? 0

  return { reservationPendingHotel, reservationPendingFood }
}

export const dataReservasDetalle = async (reservasAll: any[]): Promise<any[]> => {
  const dataPost: any[] = []

  for (const elem of reservasAll) {
    // eslint-disable-next-line no-await-in-loop
    const data = await UseGetOdata(`ReservaDetalles?$filter=ReservaId eq ${elem}`)
    const prueba: any[] = []

    data.forEach((e: { ReservaDetalleId: any; EstadoId: any }) => {
      prueba.push({
        reservaDetalleId: e.ReservaDetalleId,
        estadoActualizadoId: e.EstadoId,
      })
    })
    dataPost.push({
      ReservaId: elem,
      ReservasDetalles: prueba,
    })
  }

  return dataPost
}
